import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type Theme = "light" | "dark";
export type Palette = "quantum-dark" | "forest" | "amber" | "stone" | "sand" | "background";

const MODE_KEY = "stp_theme";
const PALETTE_KEY = "quantum_minds_theme";
const SYSTEM_KEY = "quantum_minds_theme_system";

export const PALETTES: {
  id: Palette;
  name: string;
  description: string;
  /** Preview swatch: [background, surface, accent] */
  swatch: [string, string, string];
  forcedMode?: Theme;
}[] = [
  {
    id: "quantum-dark",
    name: "Quantum Dark",
    description: "Default dark theme with a restrained emerald glow.",
    swatch: ["#000000", "#141a15", "#6FAE35"],
  },
  {
    id: "forest",
    name: "Forest",
    description: "Deep green study atmosphere with lime accents.",
    swatch: ["#122318", "#1c3324", "#A3D95B"],
  },
  {
    id: "amber",
    name: "Amber",
    description: "Warm charcoal with a focused golden accent.",
    swatch: ["#17130d", "#241d13", "#F5C451"],
  },
  {
    id: "stone",
    name: "Stone",
    description: "Clean neutral graphite. The most professional dark.",
    swatch: ["#1b1c1f", "#2a2c30", "#c9ccd2"],
  },
  {
    id: "sand",
    name: "Sand",
    description: "Light warm ivory with a soft brown accent.",
    swatch: ["#f4eee2", "#ffffff", "#C08A2E"],
    forcedMode: "light",
  },
  {
    id: "background",
    name: "Background",
    description: "Cosmic night backdrop behind your content.",
    swatch: ["#050a18", "#122444", "#8FC0FF"],
  },
];

const PALETTE_IDS = PALETTES.map((p) => p.id);

type ThemeContextValue = {
  /** Resolved light/dark mode. */
  theme: Theme;
  toggle: () => void;
  palette: Palette;
  setPalette: (p: Palette) => void;
  useSystem: boolean;
  setUseSystem: (v: boolean) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  toggle: () => {},
  palette: "background",
  setPalette: () => {},
  useSystem: false,
  setUseSystem: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function forcedModeFor(palette: Palette): Theme | undefined {
  return PALETTES.find((p) => p.id === palette)?.forcedMode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Theme>("dark");
  const [palette, setPaletteState] = useState<Palette>("background");
  const [useSystem, setUseSystemState] = useState(false);
  const [systemDark, setSystemDark] = useState(true);

  // Hydrate from storage after mount (SSR-safe).
  useEffect(() => {
    const storedMode = localStorage.getItem(MODE_KEY);
    if (storedMode === "light" || storedMode === "dark") setMode(storedMode);
    const storedPalette = localStorage.getItem(PALETTE_KEY);
    if (storedPalette && PALETTE_IDS.includes(storedPalette as Palette)) {
      setPaletteState(storedPalette as Palette);
    }
    setUseSystemState(localStorage.getItem(SYSTEM_KEY) === "1");
  }, []);

  // Track device appearance.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setSystemDark(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const theme: Theme = useMemo(() => {
    const forced = forcedModeFor(palette);
    if (forced) return forced;
    if (useSystem) return systemDark ? "dark" : "light";
    return mode;
  }, [palette, useSystem, systemDark, mode]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.dataset["theme"] = palette;
    const swatch = PALETTES.find((p) => p.id === palette)?.swatch[0];
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? (swatch ?? "#000000") : "#f7f8fb");
  }, [theme, palette]);

  const toggle = useCallback(() => {
    setUseSystemState(false);
    localStorage.setItem(SYSTEM_KEY, "0");
    let requested: Theme = "dark";
    setMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      requested = next;
      localStorage.setItem(MODE_KEY, next);
      return next;
    });
    // A palette locked to the opposite mode cannot honour the request.
    setPaletteState((prev) => {
      const forced = forcedModeFor(prev);
      if (!forced || forced === requested) return prev;
      localStorage.setItem(PALETTE_KEY, "quantum-dark");
      return "quantum-dark";
    });
  }, []);

  const setPalette = useCallback((p: Palette) => {
    setPaletteState(p);
    localStorage.setItem(PALETTE_KEY, p);
  }, []);

  const setUseSystem = useCallback((v: boolean) => {
    setUseSystemState(v);
    localStorage.setItem(SYSTEM_KEY, v ? "1" : "0");
  }, []);

  const value = useMemo(
    () => ({ theme, toggle, palette, setPalette, useSystem, setUseSystem }),
    [theme, toggle, palette, setPalette, useSystem, setUseSystem],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
